import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatDate, formatUSD } from "@/lib/utils";
import { toast } from "sonner";
import {
  ArrowRight,
  CheckCircle2,
  Loader2,
  Wifi,
  ShieldAlert,
  RefreshCw,
  Activity,
  Settings2,
} from "lucide-react";

const OUTCOME_STYLES = {
  success: {
    cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: CheckCircle2,
    label: "Success",
  },
  accepted: {
    cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: CheckCircle2,
    label: "Accepted",
  },
  unavailable: {
    cls: "bg-amber-50 text-amber-700 border-amber-200",
    icon: Wifi,
    label: "Unavailable",
  },
  insufficient_balance: {
    cls: "bg-amber-50 text-amber-700 border-amber-200",
    icon: Wifi,
    label: "Low balance",
  },
  timeout_not_found: {
    cls: "bg-amber-50 text-amber-700 border-amber-200",
    icon: Wifi,
    label: "Timeout · safe",
  },
  timeout_pending: {
    cls: "bg-slate-100 text-slate-700 border-slate-200",
    icon: Wifi,
    label: "Timeout · pending",
  },
  error: {
    cls: "bg-red-50 text-red-700 border-red-200",
    icon: ShieldAlert,
    label: "Error",
  },
  business_error: {
    cls: "bg-red-50 text-red-700 border-red-200",
    icon: ShieldAlert,
    label: "Rejected",
  },
  provider_failed: {
    cls: "bg-red-50 text-red-700 border-red-200",
    icon: ShieldAlert,
    label: "Provider failed",
  },
};

function AttemptBadge({ outcome }) {
  const style = OUTCOME_STYLES[outcome] || {
    cls: "bg-slate-100 text-slate-700 border-slate-200",
    icon: Activity,
    label: outcome || "—",
  };
  const Icon = style.icon;
  return (
    <span
      className={
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border " +
        style.cls
      }
    >
      <Icon className="w-3 h-3" />
      {style.label}
    </span>
  );
}

function ActionBadge({ action }) {
  const config = {
    PROVIDER_HEALTHCHECK: { cls: "bg-sky-50 text-sky-700 border-sky-200", label: "Health check", icon: Activity },
    PROVIDER_CONFIG_UPDATE: { cls: "bg-violet-50 text-violet-700 border-violet-200", label: "Config update", icon: Settings2 },
    PROVIDER_PRIORITIES_UPDATE: { cls: "bg-indigo-50 text-indigo-700 border-indigo-200", label: "Priority update", icon: Settings2 },
    PROVIDER_SETTINGS_UPDATE: { cls: "bg-teal-50 text-teal-700 border-teal-200", label: "Settings", icon: Settings2 },
  }[action] || { cls: "bg-slate-100 text-slate-700 border-slate-200", label: action, icon: Settings2 };
  const Icon = config.icon;
  return (
    <span
      className={
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border " +
        config.cls
      }
    >
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

function formatActorLabel(row) {
  if (!row.actor_username) return "—";
  const parts = [];
  if (row.actor_name) parts.push(row.actor_name);
  else parts.push(row.actor_username);
  const meta = [];
  if (row.actor_role) meta.push(row.actor_role.replace("_", " "));
  if (row.actor_code) meta.push(`#${row.actor_code}`);
  return meta.length ? `${parts.join(" ")} — ${meta.join(" ")}` : parts.join(" ");
}

function PayloadCell({ action, payload }) {
  if (!payload) return <span className="text-slate-400">—</span>;
  if (action === "PROVIDER_HEALTHCHECK") {
    return (
      <span className="text-xs text-slate-600">
        {payload.healthy ? "healthy" : "unhealthy"}
        {payload.latency_ms != null ? ` · ${payload.latency_ms}ms` : ""}
        {payload.balance != null
          ? ` · Balance ${formatUSD(payload.balance)} ${payload.currency || ""}`
          : ""}
        {payload.detail && !payload.healthy ? ` · ${payload.detail}` : ""}
      </span>
    );
  }
  if (action === "PROVIDER_PRIORITIES_UPDATE") {
    const changes = payload.changes || {};
    const rows = Object.entries(changes);
    if (rows.length === 0) return <span className="text-slate-400">No changes</span>;
    return (
      <div className="text-xs space-y-0.5">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center gap-1 flex-wrap">
            <span className="font-bold text-slate-800">{k}</span>
            <span className="text-slate-500">
              #{v.before?.priority ?? "?"} · {v.before?.enabled ? "on" : "off"}
            </span>
            <span className="text-slate-400">→</span>
            <span className="text-indigo-700 font-semibold">
              #{v.after?.priority ?? "?"} · {v.after?.enabled ? "on" : "off"}
            </span>
          </div>
        ))}
      </div>
    );
  }
  const patch = payload.patch || payload;
  const parts = Object.entries(patch).map(([k, v]) => (
    <span key={k} className="mr-2">
      <span className="font-semibold text-slate-700">{k}</span>
      <span className="text-slate-500">={String(v)}</span>
    </span>
  ));
  return <span className="text-xs">{parts}</span>;
}

export default function FailoverHistoryPanel() {
  const [failovers, setFailovers] = useState([]);
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [{ data: f }, { data: c }] = await Promise.all([
        api.get("/admin/providers/failover-log?limit=50"),
        api.get("/admin/providers/config-changes?limit=50"),
      ]);
      setFailovers(f.items || []);
      setChanges(c.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load history");
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 flex items-center gap-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading history…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="failover-history-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">
            Failover history
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Recent topups whose primary provider was skipped and the fallback
            chain kicked in. Nothing here means every topup was served by the
            top-priority provider on the first try.
          </div>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
          data-testid="failover-refresh-btn"
        >
          {refreshing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
          Refresh
        </button>
      </div>

      {/* Failover events */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {failovers.length === 0 ? (
          <div
            className="px-5 py-8 text-center text-sm text-slate-500"
            data-testid="failover-empty"
          >
            No failover events recorded yet — the primary provider handled
            every recent topup on the first try.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                <tr>
                  <th className="px-4 py-3 text-left">When</th>
                  <th className="px-4 py-3 text-left">Destination</th>
                  <th className="px-4 py-3 text-right">Send</th>
                  <th className="px-4 py-3 text-left">Attempt chain</th>
                  <th className="px-4 py-3 text-left">Winner</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {failovers.map((row) => (
                  <tr
                    key={row.topup_id}
                    className="hover:bg-slate-50"
                    data-testid={`failover-row-${row.topup_id}`}
                  >
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                      {formatDate(row.created_at)}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="font-semibold">{row.operator_name || "—"}</div>
                      <div className="text-xs text-slate-500">
                        {row.country_code} · {row.phone_masked}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900 whitespace-nowrap">
                      {formatUSD(row.send_value_usd || 0)} USD
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {row.attempts.map((a, i) => (
                          <React.Fragment key={i}>
                            <div className="inline-flex items-center gap-1">
                              <span className="text-xs font-semibold text-slate-700">
                                {a.provider_key}
                              </span>
                              <AttemptBadge outcome={a.outcome} />
                            </div>
                            {i < row.attempts.length - 1 && (
                              <ArrowRight className="w-3 h-3 text-slate-400" />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                      {row.attempts.some((a) => a.detail) && (
                        <div className="text-[11px] text-slate-500 mt-1">
                          {row.attempts
                            .filter((a) => a.detail)
                            .map((a, i) => (
                              <div key={i}>
                                <span className="font-semibold">
                                  {a.provider_key}:
                                </span>{" "}
                                {a.detail}
                              </div>
                            ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold text-slate-900">
                        {row.final_provider || "—"}
                      </span>
                      <div
                        className={
                          "text-[10px] font-semibold uppercase tracking-widest mt-0.5 " +
                          (row.final_status === "SUCCESS"
                            ? "text-emerald-600"
                            : row.final_status === "FAILED"
                            ? "text-red-600"
                            : "text-slate-500")
                        }
                      >
                        {row.final_status}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Config changes */}
      <div>
        <div className="text-sm font-semibold text-slate-900">
          Configuration changes
        </div>
        <div className="text-xs text-slate-500 mt-0.5 mb-3">
          Every provider enable / disable / priority change and manual health
          check by the Master Admin, in reverse chronological order.
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {changes.length === 0 ? (
            <div
              className="px-5 py-8 text-center text-sm text-slate-500"
              data-testid="config-changes-empty"
            >
              No configuration changes logged yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                  <tr>
                    <th className="px-4 py-3 text-left">When</th>
                    <th className="px-4 py-3 text-left">Action</th>
                    <th className="px-4 py-3 text-left">Provider</th>
                    <th className="px-4 py-3 text-left">Details</th>
                    <th className="px-4 py-3 text-left">Actor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {changes.map((row, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                        {formatDate(row.at)}
                      </td>
                      <td className="px-4 py-3">
                        <ActionBadge action={row.action} />
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-900">
                        {row.target_key}
                      </td>
                      <td className="px-4 py-3">
                        <PayloadCell action={row.action} payload={row.payload} />
                      </td>
                      <td className="px-4 py-3 text-slate-700 text-xs">
                        {formatActorLabel(row)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
